/**
 * E07-F11 — Jev triage on the Review / AI-check screens: DOM-free presentation rules.
 *
 * Spec: docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.3 (buckets, column,
 * filter, default «check first» order, confirmation), §5.5 (same-company hint + «Link»).
 *
 *   T02 — the «Triage» column (bucket + reason / queued / running / Jev unavailable), the bucket
 *         filter, the bucket order (check first on top), the confirmation numbers;
 *   T03 — the same-company hints a row shows (a hint only; the link changes on a click);
 *   T04 — the AI-check preselection: rows of the top bucket, capped by the volume limit.
 */
import {
  TRIAGE_BUCKETS,
  type ReviewRow,
  type RowTriage,
  type SameCompanyHint,
  type TriageBucket,
  type TriagePreviewResponse,
} from '@eer/shared';

/** UI labels of the buckets (the app UI is English). */
export const TRIAGE_BUCKET_LABEL: Readonly<Record<TriageBucket, string>> = {
  check_first: 'Check first',
  check: 'Check',
  doubtful: 'Doubtful',
  likely_junk: 'Likely junk',
};

/** Chip palette per bucket. */
export const TRIAGE_BUCKET_CHIP: Readonly<Record<TriageBucket, string>> = {
  check_first: 'bg-green-100 text-green-800 border-green-300',
  check: 'bg-sky-50 text-sky-800 border-sky-200',
  doubtful: 'bg-amber-50 text-amber-800 border-amber-200',
  likely_junk: 'bg-slate-100 text-slate-500 border-slate-300',
};

/** Filter value: every row, one bucket, or rows without a triage bucket. */
export const TRIAGE_FILTER_ALL = 'all' as const;
export const TRIAGE_FILTER_NONE = 'none' as const;
export type ReviewTriageFilter =
  | typeof TRIAGE_FILTER_ALL
  | typeof TRIAGE_FILTER_NONE
  | TriageBucket;

/** The finished bucket of a row (null while queued / running / unavailable / never triaged). */
export function triageBucket(row: Pick<ReviewRow, 'triage'>): TriageBucket | null {
  const t = row.triage;
  return t && t.status === 'done' ? t.bucket : null;
}

/** Does any row carry a triage result or a triage in flight? Pure. */
export function hasTriage(rows: readonly Pick<ReviewRow, 'triage'>[]): boolean {
  return rows.some((r) => Boolean(r.triage));
}

/** Is a triage running on the row right now (queued or running)? */
export function triageInFlight(row: Pick<ReviewRow, 'triage'>): boolean {
  const s = row.triage?.status;
  return s === 'queued' || s === 'running';
}

/**
 * Order key of a row under the triage sort: check first → check → not triaged → doubtful →
 * likely junk. A row without a bucket is unknown — better than «doubtful», worse than «check».
 */
export function triageBucketRank(row: Pick<ReviewRow, 'triage'>): number {
  const b = triageBucket(row);
  if (b === 'check_first') return 0;
  if (b === 'check') return 1;
  if (b === 'doubtful') return 3;
  if (b === 'likely_junk') return 4;
  return 2;
}

/** Does the row pass the bucket filter? Pure. */
export function matchesTriageFilter(
  row: Pick<ReviewRow, 'triage'>,
  filter: ReviewTriageFilter | undefined,
): boolean {
  if (!filter || filter === TRIAGE_FILTER_ALL) return true;
  const b = triageBucket(row);
  return filter === TRIAGE_FILTER_NONE ? b === null : b === filter;
}

export interface TriageFilterOption {
  value: ReviewTriageFilter;
  label: string;
  count: number;
}

/** The bucket filter options with counts over the FULL list (empty buckets kept, «none» last). */
export function triageFilterOptions(
  rows: readonly Pick<ReviewRow, 'triage'>[],
): TriageFilterOption[] {
  const out: TriageFilterOption[] = [
    { value: TRIAGE_FILTER_ALL, label: 'All', count: rows.length },
  ];
  for (const b of TRIAGE_BUCKETS) {
    out.push({
      value: b,
      label: TRIAGE_BUCKET_LABEL[b],
      count: rows.filter((r) => triageBucket(r) === b).length,
    });
  }
  out.push({
    value: TRIAGE_FILTER_NONE,
    label: 'Not triaged',
    count: rows.filter((r) => triageBucket(r) === null).length,
  });
  return out;
}

/** What the «Triage» cell shows. */
export interface TriageCell {
  kind: 'none' | 'queued' | 'running' | 'unavailable' | 'done';
  label: string;
  /** Short reason assembled by code («distributor, 0.91»), or the error of an unavailable row. */
  detail: string | null;
  chipClass: string;
  bucket: TriageBucket | null;
}

/** The «Triage» cell of a row. Pure. */
export function triageCell(row: Pick<ReviewRow, 'triage'>): TriageCell {
  const t: RowTriage | null | undefined = row.triage;
  const neutral = 'bg-slate-50 text-slate-500 border-slate-200';
  if (!t) return { kind: 'none', label: '—', detail: null, chipClass: '', bucket: null };
  if (t.status === 'queued') {
    return { kind: 'queued', label: 'Queued', detail: null, chipClass: neutral, bucket: null };
  }
  if (t.status === 'running') {
    return { kind: 'running', label: 'Triaging…', detail: null, chipClass: neutral, bucket: null };
  }
  if (t.status === 'unavailable') {
    return {
      kind: 'unavailable',
      label: 'Jev unavailable',
      detail: t.error ?? 'The Jev call failed — re-triage the row to try again.',
      chipClass: 'bg-red-50 text-red-700 border-red-200',
      bucket: null,
    };
  }
  const bucket = t.bucket;
  return {
    kind: 'done',
    label: bucket ? TRIAGE_BUCKET_LABEL[bucket] : '—',
    detail: t.reason,
    chipClass: bucket ? TRIAGE_BUCKET_CHIP[bucket] : neutral,
    bucket,
  };
}

/** USD for the confirmation («$0.42»; sub-cent prices keep 4 decimals). */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '$0.00';
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;
}

/** The lines of the triage confirmation dialog. Pure. */
export interface TriageConfirmSummary {
  rows: number;
  viaSearch: number;
  ambiguousPairs: number;
  price: string;
  /** «estimate» note when there is no call history (the conservative constant). */
  priceNote: string;
  canStart: boolean;
}

export function triageConfirmSummary(preview: TriagePreviewResponse): TriageConfirmSummary {
  const priceNote = preview.priceIsFallbackEstimate
    ? 'Estimate — no triage history yet, a conservative price per row is used.'
    : `Estimate — average of ${preview.priceSampleSize} past triage call${
        preview.priceSampleSize === 1 ? '' : 's'
      } (${formatUsd(preview.costPerRowUsd)} per row).`;
  return {
    rows: preview.rows,
    viaSearch: preview.viaSearch,
    ambiguousPairs: preview.ambiguousPairs ?? 0,
    price: formatUsd(preview.estimatedCostUsd),
    priceNote,
    // Spec §5.3: the run takes the rows; the ambiguous Library pairs of THOSE rows are resolved
    // inside the same run (§5.5), so without a row there is nothing to start.
    canStart: preview.rows > 0,
  };
}

/** The triage progress line: «12 of 40 triaged · 2 Jev unavailable · $0.31». Pure. */
export interface TriageProgressView {
  active: boolean;
  finished: number;
  total: number;
  unavailable: number;
  /** 0..100, rounded. */
  percent: number;
  text: string;
}

export function triageProgressView(p: {
  active: boolean;
  total: number;
  done: number;
  unavailable: number;
  costUsd: number;
}): TriageProgressView {
  const finished = p.done + p.unavailable;
  const percent = p.total > 0 ? Math.round((finished / p.total) * 100) : 0;
  const parts = [`${finished} of ${p.total} triaged`];
  if (p.unavailable > 0) parts.push(`${p.unavailable} Jev unavailable`);
  if (p.costUsd > 0) parts.push(formatUsd(p.costUsd));
  return {
    active: p.active,
    finished,
    total: p.total,
    unavailable: p.unavailable,
    percent,
    text: parts.join(' · '),
  };
}

// ---------------------------------------------------------------------------
// T03 — same-company hints
// ---------------------------------------------------------------------------

/** A hint the row shows: the text and the company the «Link» button points the row at. */
export interface SameCompanyHintView {
  companyId: number;
  companyName: string;
  relation: 'same' | 'division';
  probability: number | null;
  text: string;
}

/** «0.93» — the probability as the hint prints it. */
export function formatProbability(p: number | null): string {
  return p === null || !Number.isFinite(p) ? '' : p.toFixed(2);
}

/**
 * The hints worth showing: Jev answered (`ok`) «same» or «division». Once a person linked the row
 * (`libraryLink`), the decision is taken — no hint is shown any more. Pure.
 */
export function sameCompanyHintViews(
  row: Pick<ReviewRow, 'sameCompany' | 'libraryLink'>,
): SameCompanyHintView[] {
  if (row.libraryLink) return [];
  const hints: readonly SameCompanyHint[] = row.sameCompany?.hints ?? [];
  const out: SameCompanyHintView[] = [];
  for (const h of hints) {
    if (h.status !== 'ok') continue;
    if (h.relation !== 'same' && h.relation !== 'division') continue;
    const p = formatProbability(h.probability);
    const text =
      h.relation === 'same'
        ? `Possibly the same company as ${h.companyName} in Library${p ? ` (${p})` : ''}`
        : `Possibly a division of ${h.companyName}${p ? ` (${p})` : ''}`;
    out.push({
      companyId: h.companyId,
      companyName: h.companyName,
      relation: h.relation,
      probability: h.probability,
      text,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// T04 — AI-check preselection of the top bucket
// ---------------------------------------------------------------------------

/**
 * The rows the AI-check screen ticks by default: ONLY rows in «check first», in list order,
 * at most `limit` of them. No triage on the list → null (keep the previous behaviour). Pure.
 */
export function triagePreselection<T extends { id: number; triage?: RowTriage | null }>(
  rows: readonly T[],
  limit: number,
): number[] | null {
  if (!hasTriage(rows)) return null;
  const cap = Math.max(0, Math.floor(limit));
  return rows
    .filter((r) => triageBucket(r) === 'check_first')
    .slice(0, cap)
    .map((r) => r.id);
}

/**
 * The AI-check screen's initial selection: {@link triagePreselection} over the rows NOT yet
 * checked (`aiStatus === 'idle'`) — a preselect never re-charges a company already checked or
 * running. No triage on the list → null (the screen starts with nothing ticked, as before). Pure.
 */
export function aiCheckPreselection<
  T extends { id: number; aiStatus: string; triage?: RowTriage | null },
>(rows: readonly T[], limit: number): number[] | null {
  if (!hasTriage(rows)) return null;
  return (
    triagePreselection(
      rows.filter((r) => r.aiStatus === 'idle'),
      limit,
    ) ?? []
  );
}
